import { useState, useEffect, useRef, useMemo } from "react";
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useNavigate } from "react-router-dom";

import { useDrawerContext } from "../../context/drawerContext";

import Button from "../Button";

import VoiceNoteIcon from "/public/images/icons/voiceNoteRecord.svg?react";
import RecordingVoiceNoteIcon from "/public/images/icons/recordingVoiceNote.svg?react";
import BinIcon from "/public/images/icons/trashRed.svg?react";
import CloseIcon from "/public/images/icons/close.svg?react";
import PauseVoiceNoteIcon from "/public/images/icons/VoiceNotePause.svg?react";
import PlayVoiceNoteIcon from "/public/images/icons/voiceNotePlay.svg?react";
import { LogErrors } from "../../utils/LogErrors";
import { useUserContext } from "../../context/userContext";
 
export default function VoiceNote({ existingAudio, subscription = null, label = true, editable }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const { setOpen, setDrawerContent } = useDrawerContext();
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const [time, setTime] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioRecordingBlob, setAudioRecordingBlob] = useState(null);
    const [metadataLoaded, setMetadataLoaded] = useState(false);
    const [hasRecording, setHasRecording] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const audioRef = useRef(null);
    const [removed, setRemoved] = useState(false);
    const { startRecording, stopRecording, recordingBlob, recordingTime } = useAudioRecorder();
    const { user, logout, updateTokens } = useUserContext();

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
    };

    const createFromRecordingBlob = async (recordingBlob) => {
        const base64String = await blobToBase64(recordingBlob);
        setAudioRecordingBlob(base64String);
        const url = URL.createObjectURL(recordingBlob);
        setAudioUrl(url);
        setMetadataLoaded(false); // Reset metadata loaded state
        setHasRecording(true);

        return () => URL.revokeObjectURL(url);
    }

    useEffect( () => {
        if (recordingBlob) {
            createFromRecordingBlob(recordingBlob);
        }
    }, [recordingBlob]);

    useEffect(() => {
        const audioElement = audioRef.current;

        if (!audioElement) return;

        const handleLoadedMetadata = () => {
            setMetadataLoaded(true);
            setProgress(0);
        };

        const updateProgress = () => {
            if (!audioElement || !metadataLoaded) return;

            const currentTime = audioElement.currentTime;
            const duration = audioElement.duration;
            setTime(currentTime);
            setProgress((currentTime / duration) * 100);
        };

        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.addEventListener('timeupdate', updateProgress);
        audioElement.addEventListener('ended', () => setIsPlaying(false));

        return () => {
            audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audioElement.removeEventListener('timeupdate', updateProgress);
            audioElement.removeEventListener('ended', () => setIsPlaying(false));
        };
    }, [audioUrl, metadataLoaded, existingAudio]);

    const togglePlayPause = (e) => {
        e.preventDefault();
        if (!audioRef.current || (!audioUrl && !existingAudio) || !metadataLoaded) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play().catch((error) => {
                LogErrors(user, logout, updateTokens, 'Playback failed:', error);
            });
        }

        setIsPlaying(!isPlaying);
    };

    const handleRecording = () => {
        // setRemoved(false);
        
        if (isRecording) {
            stopRecording();
            setIsRecording(false);
            setRemoved(false);
        } else {
            // Start countdown before recording
            let count = 3;
            setCountdown(count);
            const countdownInterval = setInterval(() => {
                count -= 1;
                setCountdown(count);
                if (count === 0) {
                    clearInterval(countdownInterval);
                    setCountdown(null);
                    startRecording();
                    setIsRecording(true);
                    setTime(0);
                }
            }, 1000);
        }
    };

    useEffect(() => {
        let timer;
        if (isRecording && time < 90) {
            timer = setInterval(() => {
                setTime(prev => prev + 1);
            }, 1000);
        }

        if (time >= 90) {
            stopRecording();
            setIsRecording(false);
        }

        return () => clearInterval(timer);
    }, [isRecording, time]);

    const formatTime = (seconds) => {
        const roundedSeconds = Math.round(seconds);
        const minutes = Math.floor(roundedSeconds / 60);
        const remainingSeconds = roundedSeconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const handleDeleteFileUpload = () => {
        setRemoved(true);
        setIsPlaying(false);
        setAudioUrl(null);  // Reset the audio URL
        stopRecording();
        setIsRecording(false);  // Reset recording state
        setHasRecording(false);
        setTime(0);
        setProgress(0);
        
        // Retrieve the JSON object from localStorage and parse it
        const currentStep2Answers = JSON.parse(localStorage.getItem("activeBandStep2"));

        if (currentStep2Answers) {
            // Modify the object
            currentStep2Answers.uploadedVoiceNote = null;

            // Save the modified object back to localStorage
            localStorage.setItem("activeBandStep2", JSON.stringify(currentStep2Answers));
        }
    };

    const bronzeSubcriptionDrawer = () => {
        setOpen(true);
        setDrawerContent(
            <div className="w-full">
              <div className="flex items-center justify-between w-full">
                <br />
                <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                  UPGRADE
                </h2>
                <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
              </div>
              <div className="mt-6 flex flex-col gap-4 justify-stretch">
                <h3 className="text-[24px]">Upgrade subscription</h3>
                <p className=" text-sportsDarkGrey">Based on your bronze subscription level, you can&apos;t record voice notes. To record voice notes, please upgrade your subscription.</p>
                  <Button variant="primary" onClick={() => { setOpen(false);navigate('/upgrade')}}>Upgrade Subscription</Button>
                  <Button variant="quaternary" onClick={() => { setOpen(false);}}>Close</Button>
              </div>
            </div>
        );
    }

    const cachedAudioUrl = useMemo(() => {
        return existingAudio ? `${existingAudio}?t=${Date.now()}` : null;
      }, [existingAudio]);

    return (
        <div className="flex flex-col gap-2">
            {label && (
                <label className={`flex flex-col gap-1`}>
                    <div>
                        <span className="text-sportsBlack font-medium text-base leading-[26px] mb-1">
                            Voice Note{!hasRecording && " (max 90 seconds)"}{isRecording && ` • ${formatTime(recordingTime)}`}
                        </span>
                    </div>
                </label>
            )}

            {((!hasRecording && !existingAudio) || (!hasRecording && removed)) && (
                <Button 
                    variant="tertiary"
                    className="w-full font-semibold"
                    onClick={() => {
                        (subscription && subscription === 1) ? (
                            bronzeSubcriptionDrawer()
                        ) : (
                            !countdown ? (
                                handleRecording()
                            ) : null
                        );
                    }}
                    status={(subscription && subscription === 1) ? "disabled" : "null"}
                >
                    {countdown ? (
                        <>
                            <RecordingVoiceNoteIcon />
                            {countdown}...
                        </>
                    ) : isRecording ? (
                        <>
                            <RecordingVoiceNoteIcon />
                            Stop Recording
                        </>
                    ) : (
                        <>
                            <VoiceNoteIcon />
                            Record Voice Note
                        </>
                    )}
                </Button>
            )}
            
            {(audioUrl || existingAudio) && (
                <div className="flex flex-col gap-2">
                    {!removed && (
                        <div className="w-full h-12 bg-sportsLightGrey py-[14px] px-[17px] flex gap-x-[10px] items-center rounded">

                            <div className="relative">
                                <button
                                    className="w-5 h-5 bg-no-repeat bg-center bg-cover bg-sportsLightGrey"
                                    onClick={togglePlayPause}
                                />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                                    {isPlaying ? (
                                        <PauseVoiceNoteIcon />
                                    ) : (
                                        <PlayVoiceNoteIcon />
                                    )}
                                </div>
                            </div>

                            {isPlaying && <span className="text-base text-sportsBlack font-semibold">{formatTime(time)}</span>}

                            <div className="relative w-full h-[5px] bg-[#E3E3E3]">
                                <div className="absolute top-0 left-0 h-[5px] bg-sportsBlack" style={{ width: `${progress}%` }}></div>
                            </div>

                            <audio ref={audioRef} src={audioUrl || cachedAudioUrl} preload="auto"/>
                        </div>
                    )}
                    
                    {editable && (
                        <>
                            <input type="hidden" value={audioRecordingBlob ? audioRecordingBlob : existingAudio ? existingAudio : null } name="uploadedVoiceNote" />
                            <input type="hidden" value={removed} name="voiceNoteRemoved" />
                        </>
                    )}

                    {(!removed && editable) && (
                        <div className="flex flex-col gap-2">
                            <Button
                                variant="tertiary"
                                status="nobg"
                                onClick={handleDeleteFileUpload}
                                className="mt-2 mb-2 text-sportsRed w-full"
                            >
                                <BinIcon className="w-[24px] [&>path]:stroke-sportsRed" />
                                Remove
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
